import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Crown, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonitorForm = ({ monitor, onSubmit, onClose, token }) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    check_interval: 300, // 5 minutes
    timeout: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);
  const [packages, setPackages] = useState({});
  const [user, setUser] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  useEffect(() => {
    if (monitor) {
      setFormData({
        name: monitor.name,
        url: monitor.url,
        check_interval: monitor.check_interval,
        timeout: monitor.timeout
      });
    }
    fetchUserInfo();
    fetchPackages();
  }, [monitor]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/subscription/packages`);
      setPackages(response.data);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  };

  const handleUpgrade = async (packageId, paymentGateway = 'stripe', currency = 'usd') => {
    setUpgradeLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: packageId,
        payment_gateway: paymentGateway,
        currency: currency,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (paymentGateway === 'stripe') {
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else if (paymentGateway === 'razorpay') {
        const subscription = response.data;
        const options = {
          key: subscription.key_id,
          subscription_id: subscription.subscription_id,
          name: 'URL Monitor Pro',
          description: `Subscription to ${packages[packageId]?.name}`,
          theme: { color: '#3B82F6' },
          handler: function(response) {
            window.location.href = `${originUrl}/settings?subscription_id=${subscription.subscription_id}&success=true&gateway=razorpay`;
          },
          modal: {
            ondismiss: function() {
              setUpgradeLoading(false);
            }
          }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        setUpgradeLoading(false);
      }
    } catch (error) {
      setUpgradeLoading(false);
      setError(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowUpgradeOptions(false);

    // Basic URL validation
    try {
      new URL(formData.url);
    } catch {
      setError('Please enter a valid URL (including http:// or https://)');
      setLoading(false);
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (err) {
      const errorMessage = err.message;
      
      // Check if it's a monitor limit error
      if (errorMessage.includes('Monitor limit reached') || errorMessage.includes('plan allows')) {
        setShowUpgradeOptions(true);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'check_interval' || name === 'timeout' ? parseInt(value) : value
    }));
  };

  const getUpgradeOptions = () => {
    const currentTier = user?.subscription_tier || 'free';
    
    if (currentTier === 'free') {
      return Object.entries(packages);
    } else if (currentTier === 'pro') {
      return packages.business ? [['business', packages.business]] : [];
    }
    return [];
  };

  const renderUpgradeOptions = () => {
    const upgradeOptions = getUpgradeOptions();
    
    if (upgradeOptions.length === 0) {
      return (
        <div className="text-center py-6">
          <p className="text-gray-600 mb-4">You're already on the highest tier available!</p>
          <button
            onClick={onClose}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="text-center mb-6">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade to Add More Monitors</h3>
          <p className="text-gray-600">Choose a plan that fits your monitoring needs</p>
        </div>

        {upgradeOptions.map(([packageId, packageInfo]) => (
          <div key={packageId} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold text-gray-900">{packageInfo.name}</h4>
              <div className="text-right">
                <p className="text-xl font-bold text-blue-600">${packageInfo.price_usd}</p>
                <p className="text-sm text-gray-500">per month</p>
              </div>
            </div>
            
            <ul className="text-sm text-gray-600 space-y-1 mb-4">
              {packageInfo.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <ArrowRight className="w-3 h-3 mr-2 text-green-500" />
                  {feature}
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              <button
                onClick={() => handleUpgrade(packageId, 'stripe', 'usd')}
                disabled={upgradeLoading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
              >
                {upgradeLoading ? 'Processing...' : `Upgrade with Card ($${packageInfo.price_usd}/mo)`}
              </button>
              
              <button
                onClick={() => handleUpgrade(packageId, 'razorpay', 'inr')}
                disabled={upgradeLoading}
                className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm"
              >
                {upgradeLoading ? 'Processing...' : `Upgrade with UPI (₹${packageInfo.price_inr}/mo)`}
              </button>
            </div>
          </div>
        ))}

        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowUpgradeOptions(false)}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            ← Back to Add Monitor
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {showUpgradeOptions ? 'Upgrade Your Plan' : monitor ? 'Edit Monitor' : 'Add New Monitor'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {showUpgradeOptions ? (
            renderUpgradeOptions()
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monitor Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="e.g., My Website"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="https://example.com"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Include http:// or https://
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check Interval
                  </label>
                  <select
                    name="check_interval"
                    value={formData.check_interval}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  >
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                    <option value={600}>10 minutes</option>
                    <option value={900}>15 minutes</option>
                    <option value={1800}>30 minutes</option>
                    <option value={3600}>1 hour</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeout (seconds)
                  </label>
                  <select
                    name="timeout"
                    value={formData.timeout}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  >
                    <option value={10}>10 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds</option>
                  </select>
                </div>
              </div>

              {error && !showUpgradeOptions && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                  {error.includes('Monitor limit reached') && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setShowUpgradeOptions(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <Crown className="w-4 h-4 inline mr-2" />
                        Upgrade Now
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Saving...' : monitor ? 'Update Monitor' : 'Add Monitor'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitorForm;
